import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import documentService from '../services/documentService';
import patientService from '../services/patientService';

const Documents = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchPatients();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await documentService.getDocumentStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const response = await patientService.getAllPatients({ per_page: 100 });
      setPatients(response.data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientClick = (patient) => {
    // Navigate to patients page and open patient detail with documents tab
    navigate('/patients', { 
      state: { 
        openPatient: patient.id,
        openTab: 'documents' 
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />
      
      <div className="flex">
        <Sidebar />
        
        <div className="flex-1 p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Documents</h1>
            <p className="text-gray-600 mt-1">
              Patient-wise document management
            </p>
          </div>

          {/* Quick Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-sm text-indigo-600 font-medium">Total</p>
                <p className="text-2xl font-bold text-indigo-700">{stats.total_documents}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-600 font-medium">X-rays</p>
                <p className="text-2xl font-bold text-blue-700">{stats.by_type.xray}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600 font-medium">MRI</p>
                <p className="text-2xl font-bold text-purple-700">{stats.by_type.mri}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 font-medium">Reports</p>
                <p className="text-2xl font-bold text-green-700">{stats.by_type.report}</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-600 font-medium">Photos</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.by_type.photo}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 font-medium">Storage</p>
                <p className="text-2xl font-bold text-gray-700">{stats.total_storage_mb} MB</p>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">💡</span>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">How to Manage Documents</h3>
                <p className="text-sm text-blue-800">
                  Click on any patient below to view and manage their documents. You can upload, download, and organize documents directly from their profile.
                </p>
              </div>
            </div>
          </div>

          {/* Patient List */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Patients ({patients.length})
                </h2>
                <p className="text-sm text-gray-600">
                  Select a patient to manage their documents
                </p>
              </div>

              <div className="divide-y divide-gray-200">
                {patients.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() => handlePatientClick(patient)}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-xl font-semibold text-indigo-600">
                              {patient.name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-gray-900">
                            {patient.name}
                          </h3>
                          <div className="flex items-center space-x-3 text-sm text-gray-500">
                            <span>📞 {patient.phone}</span>
                            {patient.condition && (
                              <span>• {patient.condition}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-indigo-600">
                            {patient.documents_count || 0}
                          </p>
                          <p className="text-xs text-gray-500">Documents</p>
                        </div>
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;